import { regEx } from '../../../util/regex.ts';
import type { GenericVersion } from '../generic.ts';
import { GenericVersioningApi } from '../generic.ts';
import type { VersioningApi } from '../types.ts';

export const id = 'apk';
export const displayName = 'Alpine Package Keeper (APK)';
export const urls = [
  'https://wiki.alpinelinux.org/wiki/Package_policies',
  'https://wiki.alpinelinux.org/wiki/Alpine_Package_Keeper#Package_pinning',
];
export const supportsRanges = false;

// Regex with named capture groups for APK version parsing
const versionRegex = regEx(
  /^v?(?<major>[0-9]+)(?:\.(?<minor>[0-9]+))?(?:\.(?<patch>[0-9]+))?(?<extra>(?:\.[0-9]+)*)(?<letter>[a-z])?(?:(?<prereleaseType>_alpha|_beta|_pre|_rc)(?<prereleaseNum>[0-9]*))?(?:(?<packageFixType>_cvs|_svn|_git|_hg|_p)(?<packageFixNum>[0-9]*))?(?:-r(?<releaseNum>[0-9]+))?$/,
);

// Regex for splitting version strings into alphanumeric parts
const alphaNumRegex = regEx(/([a-zA-Z]+)|(\d+)/g);

export interface ApkVersion extends GenericVersion {
  /**
   * version is the main version part: it defines the version of origin software
   * that was packaged.
   */
  version: string;
  /**
   * releaseString is used to distinguish between different versions of packaging for the
   * same upstream version.
   */
  releaseString: string;
}

class ApkVersioningApi extends GenericVersioningApi {
  /**
   * Parse APK version format using apko's version parsing patterns
   * Based on `Apache-2.0` code in https://github.com/chainguard-dev/apko/blob/v0.30.35/pkg/apk/apk/version.go
   */
  protected _parse(version: string): ApkVersion | null {
    const match = versionRegex.exec(version);
    if (!match?.groups) {
      return null;
    }

    const {
      major,
      minor,
      patch,
      extra,
      letter,
      prereleaseType,
      prereleaseNum,
      packageFixType,
      packageFixNum,
      releaseNum,
    } = match.groups;

    const packageFixFull = packageFixType ? packageFixType + packageFixNum : '';

    const minorPatchStr =
      (minor ? `.${minor}` : '') + (patch ? `.${patch}` : '') + extra;

    // For version comparison, we only include the base version + package fix, not prerelease
    const versionStr = major + minorPatchStr + (letter ?? '') + packageFixFull;

    let prerelease: string | undefined;
    if (prereleaseType) {
      prerelease = prereleaseType.substring(1) + prereleaseNum;
    }

    // Extract numeric parts for major/minor/patch/extra
    const release = [parseInt(major, 10)];
    if (minor) {
      release.push(parseInt(minor, 10));
    }
    if (patch) {
      release.push(parseInt(patch, 10));
    }
    // Handle any additional version parts (e.g., 1.2.3.4.5)
    if (extra) {
      const extraParts = extra
        .substring(1)
        .split('.')
        .filter(Boolean)
        .map(Number);
      release.push(...extraParts);
    }

    // Extract release string (just the number part)
    const releaseString = releaseNum ?? '';

    return {
      version: versionStr,
      releaseString,
      release,
      prerelease,
    };
  }

  /**
   * Compare two APK versions according to Alpine Linux rules
   */
  protected override _compare(version: string, other: string): number {
    const parsed1 = this._parse(version);
    const parsed2 = this._parse(other);

    if (!(parsed1 && parsed2)) {
      return 1;
    }

    // Compare version parts (without release number)
    const versionCompare = this._compareVersionParts(
      parsed1.version,
      parsed2.version,
    );
    if (versionCompare !== 0) {
      return versionCompare;
    }

    // Compare prerelease identifiers
    const prerelease1 = parsed1.prerelease;
    const prerelease2 = parsed2.prerelease;

    if (prerelease1 || prerelease2) {
      if (!prerelease1) {
        return -1; // Version without prerelease < version with prerelease (APK-specific)
      }
      if (!prerelease2) {
        return 1; // Version with prerelease > version without (APK-specific)
      }
      // Both have prerelease identifiers, compare them
      const prereleaseCompare = prerelease1.localeCompare(prerelease2);
      if (prereleaseCompare !== 0) {
        return prereleaseCompare;
      }
    }

    // Compare release numbers
    const release1 = parsed1.releaseString || '';
    const release2 = parsed2.releaseString || '';

    // If one has a release number and the other doesn't, the one with release number is greater
    if (release1 && !release2) {
      return 1;
    }
    if (!release1 && release2) {
      return -1;
    }

    // If both have release numbers or neither has release numbers, compare them
    const releaseCompare = this._compareVersionParts(
      release1 || '0',
      release2 || '0',
    );
    return releaseCompare;
  }

  /**
   * Compare version parts using APK's version comparison rules
   */
  private _compareVersionParts(v1: string, v2: string): number {
    if (v1 === v2) {
      return 0;
    }
    const matchesv1 = v1.match(alphaNumRegex)!;
    const matchesv2 = v2.match(alphaNumRegex)!;
    const matches = Math.min(matchesv1.length, matchesv2.length);

    for (let i = 0; i < matches; i++) {
      const matchv1 = matchesv1[i];
      const matchv2 = matchesv2[i];

      if (matchv1 && /^\d+$/.test(matchv1)) {
        if (!matchv2 || !/^\d+$/.test(matchv2)) {
          return 1;
        }
        const num1 = parseInt(matchv1, 10);
        const num2 = parseInt(matchv2, 10);
        if (num1 !== num2) {
          return num1 - num2;
        }
      } else if (matchv2 && /^\d+$/.test(matchv2)) {
        return -1;
      } else if (matchv1 !== matchv2) {
        return matchv1.localeCompare(matchv2);
      }
    }

    if (matchesv1.length !== matchesv2.length) {
      const maxLength = Math.max(matchesv1.length, matchesv2.length);
      for (let i = matches; i < maxLength; i++) {
        const matchv1 = matchesv1[i];
        const matchv2 = matchesv2[i];

        if (matchv1 && /^\d+$/.test(matchv1)) {
          return 1;
        } else if (matchv2 && /^\d+$/.test(matchv2)) {
          return -1;
        } else if (matchv1) {
          return -1;
        } else {
          return 1;
        }
      }
    }

    return 0;
  }

  override isValid(version: string): boolean {
    if (!version) {
      return false;
    }
    // Strip any operators (=, >, <, ~) before validation
    const cleanVersion = version.replace(/^[=><~][=]?/, '');
    const parsed = this._parse(cleanVersion);
    return parsed !== null;
  }

  override isSingleVersion(version: string): boolean {
    if (!version) {
      return false;
    }
    // Range constraints (>, >=, <, <=, ~) are not single versions
    if (/^[><~]/.test(version)) {
      return false;
    }
    // Exact versions (starting with = or no operator) are single versions
    return this.isValid(version);
  }

  override isStable(version: string): boolean {
    if (!version) {
      return false;
    }
    // Strip any operators (=, >, <, ~) before checking stability
    const cleanVersion = version.replace(/^[=><~][=]?/, '');
    const parsed = this._parse(cleanVersion);
    if (!parsed) {
      return false;
    }
    // Consider versions without prerelease identifiers as stable
    return !parsed.prerelease;
  }

  override getSatisfyingVersion(
    versions: string[],
    range: string,
  ): string | null {
    // Handle range expressions like >5.2.37-r0, <5.2.37-r0, ~5.2.37-r0, etc.
    const rangeMatch = /^([><=~]+)(.+)$/.exec(range);
    if (!rangeMatch) {
      // If no range operator, look for exact match
      return versions.find((v) => this.equals(v, range)) ?? null;
    }

    const [, operator, targetVersion] = rangeMatch;

    // Filter versions that satisfy the range
    const satisfyingVersions = versions.filter((version) => {
      if (!this.isValid(version) || !this.isValid(targetVersion)) {
        return false;
      }

      switch (operator) {
        case '>':
          return this.isGreaterThan(version, targetVersion);
        case '>=':
          return (
            this.isGreaterThan(version, targetVersion) ||
            this.equals(version, targetVersion)
          );
        case '<':
          return this.isLessThanRange(version, targetVersion);
        case '<=':
          return (
            this.isLessThanRange(version, targetVersion) ||
            this.equals(version, targetVersion)
          );
        case '=':
        case '==':
          return this.equals(version, targetVersion);
        case '~': {
          const targetParsed = this._parse(targetVersion)!;
          const versionParsed = this._parse(version)!;

          // Must have same major and minor versions
          if (
            targetParsed.release[0] !== versionParsed.release[0] ||
            targetParsed.release[1] !== versionParsed.release[1]
          ) {
            return false;
          }

          // Version must be >= target
          return (
            this.isGreaterThan(version, targetVersion) ||
            this.equals(version, targetVersion)
          );
        }
      }
      // Fallback for unhandled operator combinations like >>, <>, ><, ~~~
      // These match the regex but aren't semantically valid APK range operators
      return false;
    });

    if (satisfyingVersions.length === 0) {
      return null;
    }

    // Return the highest satisfying version
    return satisfyingVersions.sort((a, b) => this.sortVersions(b, a))[0];
  }

  override getMajor(version: string): number | null {
    if (!version) {
      return null;
    }
    // Strip any operators (=, >, <, ~) before parsing
    const cleanVersion = version.replace(/^[=><~][=]?/, '');
    const parsed = this._parse(cleanVersion);
    return parsed?.release[0] ?? null;
  }

  override getMinor(version: string): number | null {
    if (!version) {
      return null;
    }
    // Strip any operators (=, >, <, ~) before parsing
    const cleanVersion = version.replace(/^[=><~][=]?/, '');
    const parsed = this._parse(cleanVersion);
    return parsed?.release[1] ?? null;
  }

  override getPatch(version: string): number | null {
    if (!version) {
      return null;
    }
    // Strip any operators (=, >, <, ~) before parsing
    const cleanVersion = version.replace(/^[=><~][=]?/, '');
    const parsed = this._parse(cleanVersion);
    if (!parsed) {
      return null;
    }
    return parsed.release[2] ?? null;
  }

  override getNewValue({
    currentValue,
    newVersion,
  }: {
    currentValue: string;
    rangeStrategy?: string;
    currentVersion?: string;
    newVersion: string;
  }): string | null {
    // APK packages in apko.yaml only use exact versions
    // currentValue is stored without the = operator for cleaner PR display
    const hasRevision = /-r\d+$/.test(currentValue);

    // If current version has no revision, strip revision from newVersion
    // This ensures both newValue and the displayed version are clean
    if (!hasRevision) {
      return newVersion.replace(/-r\d+$/, '');
    }

    // If current version has revision, keep revision in newVersion
    return newVersion;
  }

  // Override to provide clean version for PR titles and display
  override sortVersions(a: string, b: string): number {
    // Strip = prefix if present for comparison
    const cleanA = a.replace(/^=/, '');
    const cleanB = b.replace(/^=/, '');
    return super.sortVersions(cleanA, cleanB);
  }
}

export const api: VersioningApi = new ApkVersioningApi();

export default api;
